import DSModal from "../../views/system-design/DSModal";
import DSBox from "../../views/system-design/DSBox";
import DSTypography from "../../views/system-design/DSTypography";
import DSTextField from "../../views/system-design/DSTextField";
import DSPrimaryButton from "../../views/system-design/DSPrimaryButton";
import DSSecondaryButton from "../../views/system-design/DSSecondaryButton";
import DSCheckbox from "../../views/system-design/DSCheckBox";
import DSFormControlLabel from "../../views/system-design/DSFormControlLabel";
import DSCircularProgress from "../../views/system-design/DSCircularProgress";
import { PublicationReviewStates } from "../../api/klydo";
import { CollectionReviewStates } from "../../api/collection";
import { ReactNode, useEffect, useState } from "react";

/**
 * The verdict being submitted. This modal is shared by the two review axes —
 * klydo publication review and collection review — which have their own enums.
 */
export type ReviewVerdictState =
  | PublicationReviewStates
  | CollectionReviewStates;

export type ReviewModalProps = {
  open: boolean;
  handleClose: () => void;
  handleSubmitReview: (message: string, state: ReviewVerdictState) => void;
  loading?: boolean;
  state: ReviewVerdictState;
  price?: number;
  onPriceChange?: (value: number) => void;
  publishToShopify?: boolean;
  onPublishToShopifyChange?: (value: boolean) => void;
  warningMessage?: string;
  maxLength?: number;
  children?: ReactNode;
};

const ReviewFormModal = ({
  open,
  handleClose,
  handleSubmitReview,
  loading,
  state,
  price,
  onPriceChange,
  publishToShopify,
  onPublishToShopifyChange,
  warningMessage,
  maxLength,
  children,
}: ReviewModalProps) => {
  // A setter only: prefilling would resend the previous review's text as this verdict.
  const [message, setMessage] = useState<string>("");

  useEffect(() => {
    if (open) {
      setMessage("");
    }
  }, [open]);

  const handleSubmit = () => handleSubmitReview(message, state);
  // Approval is the only verdict this modal renders extra fields for, and each
  // axis spells it with its own enum member.
  const isApproval =
    state === PublicationReviewStates.Approved ||
    state === CollectionReviewStates.Approved;

  return (
    <DSModal
      open={open}
      onClose={loading ? undefined : handleClose}
      aria-labelledby="review-modal-title"
      aria-describedby="review-modal-description"
    >
      <DSBox
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          p: 4,
        }}
      >
        <DSTypography variant="h6" id="review-modal-title">
          Review Details
        </DSTypography>
        <DSTextField
          fullWidth
          label="Message"
          value={message}
          onChange={(e) =>
            setMessage(
              maxLength ? e.target.value.slice(0, maxLength) : e.target.value,
            )
          }
          multiline
          rows={4}
          margin="normal"
          slotProps={{
            input: {
              sx: {
                "& textarea": {
                  overflowY: "auto !important",
                },
              },
            },
          }}
        />
        {maxLength && (
          <DSTypography
            variant="caption"
            sx={{
              display: "block",
              textAlign: "right",
              color:
                message.length >= maxLength ? "error.main" : "text.secondary",
            }}
          >
            {message.length}/{maxLength}
          </DSTypography>
        )}
        {isApproval && onPriceChange && (
          <DSTextField
            fullWidth
            label="Price"
            type="number"
            value={price}
            onChange={(e) => onPriceChange(Number(e.target.value))}
            margin="normal"
            inputProps={{ min: 0, step: 0.01 }}
          />
        )}
        {isApproval && onPublishToShopifyChange && (
          <DSBox sx={{ mt: 2 }}>
            <DSFormControlLabel
              control={
                <DSCheckbox
                  checked={publishToShopify}
                  onChange={(e) => onPublishToShopifyChange(e.target.checked)}
                />
              }
              label="Publish to Shopify Marketplace"
            />
          </DSBox>
        )}
        {warningMessage && isApproval && (
          <DSBox
            sx={{
              mt: 2,
              p: 2,
              bgcolor: "#fff3e0",
              borderRadius: 1,
              border: "1px solid #ff9800",
            }}
          >
            <DSTypography sx={{ color: "#e65100", fontWeight: "bold" }}>
              {warningMessage}
            </DSTypography>
          </DSBox>
        )}
        {children}
        <DSBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mt={2}
        >
          <DSSecondaryButton disabled={loading} onClick={handleClose}>
            Close
          </DSSecondaryButton>
          <DSBox display="flex" alignItems="center" gap={1}>
            {loading && <DSCircularProgress size={20} />}
            <DSPrimaryButton disabled={loading} onClick={handleSubmit}>
              Submit
            </DSPrimaryButton>
          </DSBox>
        </DSBox>
      </DSBox>
    </DSModal>
  );
};

export default ReviewFormModal;
